import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useEngagements } from '../lib/queries'
import { useAuth } from '../auth'
import { Empty, Money, PageHead, Severity, Skel, Stat } from '../components/ui'
import { money, number, thaiDate, thaiDateTime } from '../lib/format'

// Payroll register and ghost-employee detection (part 182 extended). The page never holds a raw
// bank account or tax id: the API returns the masked forms only, and the create form is the
// single place a full value is typed.

type EmployeeStatus = 'active' | 'terminated'

interface EmployeeRow {
  id: string
  code: string
  full_name: string
  tax_id_masked: string
  bank_account_masked: string
  hired_on?: string
  status: EmployeeStatus
  monthly_satang: number
  paid_satang: number
  last_period?: string
  created_at: string
  updated_at: string
}

interface PayrollRunRow {
  id: string
  employee_id: string
  employee_code: string
  employee_name: string
  period: string
  paid_at: string
  amount_satang: number
  reference: string
  created_at: string
}

interface PayrollHit {
  rule_code: string
  rule_version: number
  title: string
  summary: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  risk_score: number
  confidence: number
  amount_at_risk_satang: number
  dedupe_key: string
}

interface ScanResult {
  generated_at: string
  engagement_id: string
  total: number
  inserted: number
  skipped: number
  counts: Record<string, number>
  hits: PayrollHit[]
  elapsed_ms: number
}

interface Draft {
  id: string
  code: string
  full_name: string
  tax_id: string
  bank_account: string
  hired_on: string
  status: EmployeeStatus
  monthly: string
}

const EMPTY_DRAFT: Draft = {
  id: '', code: '', full_name: '', tax_id: '', bank_account: '', hired_on: '', status: 'active', monthly: '',
}

const CODES = ['GHOST_NO_TAX_ID', 'GHOST_DUPLICATE_BANK', 'GHOST_VENDOR_OVERLAP', 'SALARY_OUTLIER'] as const

const CODE_LABEL: Record<string, string> = {
  GHOST_NO_TAX_ID: 'ไม่มีเลขผู้เสียภาษี',
  GHOST_DUPLICATE_BANK: 'บัญชีรับเงินซ้ำ',
  GHOST_VENDOR_OVERLAP: 'บัญชีตรงกับผู้ขาย',
  SALARY_OUTLIER: 'เงินเดือนเกินสามเท่ามัธยฐาน',
}

const CODE_HINT: Record<string, string> = {
  GHOST_NO_TAX_ID: 'พนักงานที่ยังใช้งานได้รับเงินแต่ไม่มีเลขประจำตัวผู้เสียภาษี',
  GHOST_DUPLICATE_BANK: 'พนักงานที่ยังใช้งานตั้งแต่สองคนใช้บัญชีรับเงินเดียวกัน',
  GHOST_VENDOR_OVERLAP: 'บัญชีรับเงินของพนักงานตรงกับบัญชีในทะเบียนผู้ขาย',
  SALARY_OUTLIER: 'รายการจ่ายสูงกว่าค่าสมัธยฐานของงวดเกินสามเท่า',
}

const STATUS_LABEL: Record<EmployeeStatus, string> = { active: 'ใช้งาน', terminated: 'พ้นสภาพ' }
const STATUS_TONE: Record<EmployeeStatus, string> = {
  active: 'var(--color-emerald-soft)',
  terminated: 'var(--color-ivory-dim)',
}

export function Payroll() {
  const { me } = useAuth()
  const canWrite = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const qc = useQueryClient()

  const employees = useQuery({ queryKey: ['payroll-employees'], queryFn: () => api<EmployeeRow[]>('/v1/payroll/employees') })

  const [period, setPeriod] = useState('')
  // Read every run once and filter in memory: the period dropdown must keep offering the other
  // periods after one is selected.
  const runs = useQuery({
    queryKey: ['payroll-runs'],
    queryFn: () => api<PayrollRunRow[]>('/v1/payroll/runs'),
  })
  const allRuns = runs.data ?? []
  const periods = useMemo(() => {
    const set = new Set(allRuns.map((r) => r.period))
    return Array.from(set).sort().reverse()
  }, [allRuns])
  const runRows = period ? allRuns.filter((r) => r.period === period) : allRuns

  const engagements = useEngagements()
  const engRows = engagements.data ?? []
  const [engagementId, setEngagementId] = useState('')
  const activeEngagement = engagementId || engRows[0]?.id || ''

  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [err, setErr] = useState('')
  const [scan, setScan] = useState<ScanResult | null>(null)

  const save = useMutation({
    mutationFn: (d: Draft) => apiWrite<EmployeeRow>('/v1/payroll/employees', 'POST', {
      id: d.id,
      code: d.code,
      full_name: d.full_name,
      tax_id: d.tax_id,
      bank_account: d.bank_account,
      hired_on: d.hired_on,
      status: d.status,
      monthly_satang: Math.round(Number(d.monthly || '0') * 100),
    }),
    onSuccess: () => {
      setOpen(false)
      setDraft(EMPTY_DRAFT)
      setErr('')
      void qc.invalidateQueries({ queryKey: ['payroll-employees'] })
    },
    onError: (e) => setErr((e as Error).message),
  })

  const runScan = useMutation({
    mutationFn: (id: string) => apiWrite<ScanResult>('/v1/payroll/scan', 'POST', { engagement_id: id }),
    onSuccess: (data) => setScan(data),
    onError: (e) => setErr((e as Error).message),
  })

  const rows = employees.data ?? []
  const activeCount = rows.filter((e) => e.status === 'active').length
  const monthlyTotal = rows.filter((e) => e.status === 'active').reduce((s, e) => s + e.monthly_satang, 0)
  const counts = scan?.counts ?? {}

  function startCreate() {
    if (open && draft.id === '') {
      setOpen(false)
      return
    }
    setDraft(EMPTY_DRAFT)
    setErr('')
    setOpen(true)
  }

  function startEdit(e: EmployeeRow) {
    setDraft({
      id: e.id,
      code: e.code,
      full_name: e.full_name,
      tax_id: '',
      bank_account: '',
      hired_on: e.hired_on ?? '',
      status: e.status,
      monthly: (e.monthly_satang / 100).toFixed(2),
    })
    setErr('')
    setOpen(true)
  }

  return (
    <>
      <PageHead
        title="ทะเบียนเงินเดือน"
        sub="พนักงาน งวดจ่าย และข้อตรวจพนักงานผีที่คำนวณจากทะเบียนกับหลักฐานการจ่ายจริง"
        actions={canWrite ? (
          <button className="btn btn-primary" onClick={startCreate}>
            {open ? 'ปิดฟอร์ม' : 'เพิ่มพนักงาน'}
          </button>
        ) : undefined}
      />

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat label="พนักงานที่ยังใช้งาน" value={number(activeCount)} unit={`จาก ${rows.length} คน`} />
        <Stat label="เงินเดือนรวมต่อเดือน" value={money(monthlyTotal, { compact: true })} foot="นับเฉพาะสถานะใช้งาน" />
        <Stat label="รายการจ่ายที่บันทึก" value={number(runRows.length)} unit="รายการ" foot={period || 'ทุกงวด'} />
        <Stat label="ข้อตรวจครั้งล่าสุด" value={scan ? number(scan.total) : '—'} unit="ข้อ"
          foot={scan ? `บันทึกใหม่ ${scan.inserted} · มีอยู่แล้ว ${scan.skipped}` : 'ยังไม่ได้สแกน'} />
      </div>

      {open && (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="card-label">{draft.id ? `แก้ไขพนักงาน · ${draft.code}` : 'เพิ่มพนักงานใหม่'}</div>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="p-code">รหัสพนักงาน</label>
              <input id="p-code" className="input mono" value={draft.code} maxLength={32}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                placeholder="EMP-006" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="p-name">ชื่อ-นามสกุล</label>
              <input id="p-name" className="input" value={draft.full_name}
                onChange={(e) => setDraft({ ...draft, full_name: e.target.value })}
                placeholder="ชื่อ นามสกุล" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="p-tax">เลขประจำตัวผู้เสียภาษี</label>
              <input id="p-tax" className="input mono" inputMode="numeric" maxLength={13} value={draft.tax_id}
                onChange={(e) => setDraft({ ...draft, tax_id: e.target.value.replace(/\D/g, '') })}
                placeholder={draft.id ? 'เว้นว่างเพื่อคงเดิม' : '1101700000000'} />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="p-bank">เลขบัญชีรับเงิน</label>
              <input id="p-bank" className="input mono" inputMode="numeric" maxLength={15} value={draft.bank_account}
                onChange={(e) => setDraft({ ...draft, bank_account: e.target.value.replace(/\D/g, '') })}
                placeholder={draft.id ? 'เว้นว่างเพื่อคงเดิม' : '004123456789'} />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="p-hired">วันที่เริ่มงาน</label>
              <input id="p-hired" className="input mono" type="date" value={draft.hired_on}
                onChange={(e) => setDraft({ ...draft, hired_on: e.target.value })} />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="p-status">สถานะ</label>
              <select id="p-status" className="select" style={{ width: '100%' }} value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value as EmployeeStatus })}>
                <option value="active">ใช้งาน</option>
                <option value="terminated">พ้นสภาพ</option>
              </select>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="p-salary">เงินเดือน (บาท)</label>
              <input id="p-salary" className="input mono" inputMode="decimal" value={draft.monthly}
                onChange={(e) => setDraft({ ...draft, monthly: e.target.value.replace(/[^\d.]/g, '') })}
                placeholder="45000.00" />
            </div>
          </div>
          {err && <div className="form-error">{err}</div>}
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary" disabled={save.isPending || draft.code === '' || draft.full_name === ''}
              onClick={() => { setErr(''); save.mutate(draft) }}>
              {save.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              ระบบแสดงเลขบัญชีและเลขผู้เสียภาษีเพียงสี่หลักสุดท้าย การเว้นว่างตอนแก้ไขจะคงค่าเดิมไว้
            </span>
          </div>
        </div>
      )}

      {employees.isLoading ? <Skel h={240} /> : rows.length === 0 ? (
        <Empty title="ยังไม่มีพนักงานในทะเบียน" body="เพิ่มพนักงานหรือนำเข้างวดเงินเดือนก่อนเริ่มตรวจพนักงานผี" />
      ) : (
        <div className="table-wrap card">
          <table className="data">
            <thead>
              <tr>
                <th>พนักงาน</th>
                <th>เลขผู้เสียภาษี</th>
                <th>บัญชีรับเงิน</th>
                <th className="num">เงินเดือน</th>
                <th>สถานะ</th>
                <th className="num">จ่ายสะสม</th>
                <th className="num">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td>
                    <div>{e.full_name}</div>
                    <div className="mono dim" style={{ fontSize: 11, marginTop: 3 }}>{e.code}</div>
                  </td>
                  <td className="mono dim">{e.tax_id_masked || '—'}</td>
                  <td className="mono dim">{e.bank_account_masked || '—'}</td>
                  <td className="num"><Money satang={e.monthly_satang} /></td>
                  <td>
                    <span className="chip" style={{ color: STATUS_TONE[e.status], borderColor: 'currentColor' }}>
                      {STATUS_LABEL[e.status]}
                    </span>
                  </td>
                  <td className="num">
                    <Money satang={e.paid_satang} compact />
                    <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
                      {e.last_period ? `งวดล่าสุด ${e.last_period}` : 'ยังไม่เคยจ่าย'}
                    </div>
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      {canWrite && <button className="btn btn-ghost btn-sm" onClick={() => startEdit(e)}>แก้ไข</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card card-pad" style={{ marginTop: 18 }}>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <div className="card-label">รายการจ่ายตามงวด</div>
          <div className="row" style={{ marginLeft: 'auto', gap: 8 }}>
            <select className="select" aria-label="กรองตามงวดเงินเดือน" value={period}
              onChange={(e) => setPeriod(e.target.value)} style={{ minWidth: 180 }}>
              <option value="">ทุกงวด</option>
              {periods.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        {runs.isLoading ? <Skel h={160} /> : runRows.length === 0 ? (
          <div style={{ marginTop: 16 }}>
            <Empty title="ยังไม่มีรายการจ่าย" body="บันทึกรายการเงินเดือนของงวดแรกเพื่อให้ระบบตรวจพนักงานผีได้" />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 16 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>งวด</th>
                  <th>พนักงาน</th>
                  <th>วันที่จ่าย</th>
                  <th className="num">ยอดจ่าย</th>
                  <th>เอกสารอ้างอิง</th>
                </tr>
              </thead>
              <tbody>
                {runRows.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.period}</td>
                    <td>
                      <div>{r.employee_name}</div>
                      <div className="mono dim" style={{ fontSize: 11, marginTop: 3 }}>{r.employee_code}</div>
                    </td>
                    <td className="dim">{thaiDate(r.paid_at)}</td>
                    <td className="num"><Money satang={r.amount_satang} /></td>
                    <td className="mono dim">{r.reference || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card-pad" style={{ marginTop: 18 }}>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <div className="card-label">สแกนพนักงานผี</div>
          <div className="row" style={{ marginLeft: 'auto', gap: 8 }}>
            <select className="select" aria-label="เลือกโครงการตรวจสอบ" value={activeEngagement}
              onChange={(e) => { setEngagementId(e.target.value); setScan(null) }} style={{ minWidth: 280 }}>
              {engRows.length === 0 && <option value="">ยังไม่มีโครงการ</option>}
              {engRows.map((e) => <option key={e.id} value={e.id}>{e.code} · {e.title}</option>)}
            </select>
            <button className="btn btn-primary" disabled={!activeEngagement || runScan.isPending}
              onClick={() => { setErr(''); runScan.mutate(activeEngagement) }}>
              {runScan.isPending ? 'กำลังสแกน…' : 'สแกนเงินเดือน'}
            </button>
          </div>
        </div>

        {runScan.isError && (
          <div className="form-error">{runScan.error instanceof Error ? runScan.error.message : 'สแกนไม่สำเร็จ'}</div>
        )}

        {!scan ? (
          <div style={{ marginTop: 16 }}>
            <Empty title="ยังไม่ได้สแกน" body="ระบบจะตรวจสี่ข้อ: ขาดเลขผู้เสียภาษี บัญชีรับเงินซ้ำ บัญชีตรงกับผู้ขาย และเงินเดือนเกินสามเท่ามัธยฐาน" />
          </div>
        ) : (
          <>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
              {CODES.map((code) => (
                <span key={code} className={`chip${(counts[code] ?? 0) > 0 ? ' gold' : ''}`} title={CODE_HINT[code]}>
                  {CODE_LABEL[code]} · {counts[code] ?? 0}
                </span>
              ))}
            </div>

            <div className="dim" style={{ fontSize: 11.5, marginTop: 12 }}>
              สแกนเมื่อ {thaiDateTime(scan.generated_at)} · ใช้เวลา {scan.elapsed_ms} ms ·
              พบ {scan.total} ข้อ · บันทึกใหม่ {scan.inserted} · มีอยู่แล้ว {scan.skipped}
            </div>

            {scan.hits.length === 0 ? (
              <div style={{ marginTop: 16 }}>
                <Empty title="ไม่พบข้อสงสัย" body="ทะเบียนและรายการจ่ายไม่เข้าเงื่อนไขทั้งสี่ข้อในรอบนี้" />
              </div>
            ) : (
              <div className="table-wrap" style={{ marginTop: 16 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>ข้อตรวจ</th>
                      <th>ระดับ</th>
                      <th className="num">คะแนน</th>
                      <th className="num">มูลค่าเสี่ยง</th>
                      <th>รายละเอียด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scan.hits.map((h) => (
                      <tr key={h.dedupe_key}>
                        <td>
                          <div>{CODE_LABEL[h.rule_code] ?? h.rule_code}</div>
                          <div className="mono dim" style={{ fontSize: 11, marginTop: 3 }}>{h.rule_code}</div>
                        </td>
                        <td><Severity value={h.severity} /></td>
                        <td className="num tnum">{h.risk_score}</td>
                        <td className="num"><Money satang={h.amount_at_risk_satang} compact /></td>
                        <td style={{ maxWidth: 460 }}>{h.summary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
